import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';

/**
 * DoubleScrollTable renders both top and bottom horizontal scrollbars
 * for wide data tables. Scrolling either top or bottom scrollbar
 * keeps them seamlessly in sync.
 */
const DoubleScrollTable = ({
  children,
  className = '',
  style = {},
  dependencies = [],
}) => {
  const containerRef = useRef(null);
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const updateWidth = () => {
    const bottom = bottomScrollRef.current;
    const top = topScrollRef.current;
    if (!bottom || !top || !top.firstChild) return;

    const scrollWidth = bottom.scrollWidth;
    const clientWidth = bottom.clientWidth;
    const hasOverflow = scrollWidth > clientWidth + 1;

    setIsScrollable(hasOverflow);
    top.firstChild.style.width = `${scrollWidth}px`;
  };

  useLayoutEffect(() => {
    updateWidth();
  });

  useEffect(() => {
    updateWidth();
    const bottom = bottomScrollRef.current;
    if (!bottom) return;

    const ro = new ResizeObserver(() => {
      updateWidth();
    });

    ro.observe(bottom);
    const table = bottom.querySelector('table');
    if (table) {
      ro.observe(table);
    }

    const t = setTimeout(updateWidth, 100);

    return () => {
      clearTimeout(t);
      ro.disconnect();
    };
  }, dependencies);

  // Synchronize scrollLeft between top and bottom
  useEffect(() => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (!top || !bottom) return;

    let isSyncing = false;

    const handleTopScroll = () => {
      if (isSyncing) return;
      isSyncing = true;
      bottom.scrollLeft = top.scrollLeft;
      requestAnimationFrame(() => {
        isSyncing = false;
      });
    };

    const handleBottomScroll = () => {
      if (isSyncing) return;
      isSyncing = true;
      top.scrollLeft = bottom.scrollLeft;
      requestAnimationFrame(() => {
        isSyncing = false;
      });
    };

    top.addEventListener('scroll', handleTopScroll, { passive: true });
    bottom.addEventListener('scroll', handleBottomScroll, { passive: true });

    return () => {
      top.removeEventListener('scroll', handleTopScroll);
      bottom.removeEventListener('scroll', handleBottomScroll);
    };
  }, []);

  return (
    <div className="pr-double-scroll-wrapper" ref={containerRef}>
      {/* Top horizontal scrollbar */}
      <div
        ref={topScrollRef}
        className="pr-double-scroll-top"
        style={{
          display: isScrollable ? 'block' : 'none',
        }}
        title="Scroll horizontally"
      >
        <div style={{ height: '1px' }} />
      </div>

      {/* Main Table Container (Bottom Scrollbar) */}
      <div
        ref={bottomScrollRef}
        className={`table-responsive pr-double-scroll-bottom ${className}`}
        style={{
          overflowX: 'auto',
          ...style,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default DoubleScrollTable;
